import React, { useEffect, useState } from 'react';
import axios from 'axios';
import type { FormikProps } from 'formik';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import TextFieldFormik from '~/components/TextFieldFormik';
import { reportAxiosError } from '~/Utils';

const getPolicyAIConfigUrl = (organizationId: number, lobName: string, subOrgId?: number | null) => {
  if (subOrgId) {
    return `/api/v1/policy_ai/config/organizations/${organizationId}/sub_organizations/${subOrgId}/lobs/${lobName}`;
  }
  return `/api/v1/policy_ai/config/organizations/${organizationId}/lobs/${lobName}`;
};

const validationSchema = Yup.object().shape({
  policy_search_special_instructions: Yup.string().nullable(),
});

interface EditClivePolicyDialogProps {
  onClose: () => void;
  subOrgId?: number | null;
  lobName: string;
  displayName: string;
}

interface FormValues {
  policy_search_special_instructions: string;
}

const EditClivePolicyDialog: React.FC<EditClivePolicyDialogProps> = ({ onClose, subOrgId, lobName, displayName }) => {
  const { organization } = useSysconfig() as { organization: { id: number } };
  const [initialValues, setInitialValues] = useState<FormValues>({
    policy_search_special_instructions: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(getPolicyAIConfigUrl(organization.id, lobName, subOrgId));
        setInitialValues({
          policy_search_special_instructions: response.data.policy_search_special_instructions || '',
        });
      } catch (error) {
        await reportAxiosError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [organization.id, lobName, subOrgId]);

  const handleSubmit = async (values: FormValues) => {
    try {
      await axios.put(getPolicyAIConfigUrl(organization.id, lobName, subOrgId), {
        clive_policy_config: {
          policy_search_special_instructions: values.policy_search_special_instructions.trim() || null,
        },
      });
      onClose();
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  if (loading) {
    return (
      <CardDialog
        title="Loading..."
        isDialog
        open={true}
        maxWidth="lg"
        onClose={onClose}
        footerActions={<DialogFooterActions onClickSecondary={onClose} secondaryLabel="Cancel" showPrimary={false} />}
      >
        <div />
      </CardDialog>
    );
  }

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      <EditClivePolicyDialogInner onClose={onClose} displayName={displayName} />
    </Formik>
  );
};

interface EditClivePolicyDialogInnerProps {
  onClose: () => void;
  displayName: string;
}

const EditClivePolicyDialogInner: React.FC<EditClivePolicyDialogInnerProps> = ({ onClose, displayName }) => {
  const { handleSubmit, isSubmitting }: FormikProps<FormValues> = useFormikContext();

  return (
    <CardDialog
      title={`Edit Clive Policy Configuration - ${displayName}`}
      isDialog
      open={true}
      maxWidth="lg"
      onClose={onClose}
      footerActions={
        <DialogFooterActions
          onClickPrimary={handleSubmit}
          onClickSecondary={onClose}
          primaryLabel="Save"
          showPrimary
          secondaryLabel="Cancel"
          disabled={isSubmitting}
        />
      }
    >
      <div className="flex flex-col gap-24">
        <TextFieldFormik
          name="policy_search_special_instructions"
          id="policy_search_special_instructions"
          label="Policy Search Special Instructions"
          multiline
          rows={6}
          placeholder="Enter any special instructions for policy search..."
          helperText="These instructions will be used by Clive when searching for policies"
          className="w-full"
        />
      </div>
    </CardDialog>
  );
};

export default EditClivePolicyDialog;
